// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import { createReference, getReferenceString } from '@medplum/core';
import type { CareTeam, Patient, Practitioner } from '@medplum/fhirtypes';

export interface EnsurePractitionerOnCareTeamOptions {
  decorate?: <T extends { resourceType: string }>(resource: T) => T;
}

export async function ensurePractitionerOnCareTeam(
  medplum: MedplumClient,
  patient: Patient,
  practitioner: Practitioner,
  options: EnsurePractitionerOnCareTeamOptions = {}
): Promise<CareTeam> {
  if (!patient.id || !practitioner.id) {
    throw new Error('Patient and practitioner are required to sync the care team.');
  }

  const decorate = options.decorate ?? (<T>(resource: T): T => resource);
  const patientRef = getReferenceString(patient);
  const practitionerRef = getReferenceString(practitioner);

  const existingTeam = (await medplum.searchOne('CareTeam', {
    subject: patientRef,
    _sort: '-_lastUpdated',
  })) as CareTeam | undefined;

  const existingParticipants = existingTeam?.participant ?? [];
  const alreadyPresent = existingParticipants.some((participant) => participant.member?.reference === practitionerRef);
  if (existingTeam && alreadyPresent) {
    return existingTeam;
  }

  if (existingTeam) {
    return medplum.updateResource(
      decorate({
        ...existingTeam,
        participant: [
          ...existingParticipants,
          {
            member: createReference(practitioner),
          },
        ],
      })
    );
  }

  return medplum.createResource(
    decorate({
      resourceType: 'CareTeam',
      status: 'active',
      subject: createReference(patient),
      participant: [
        {
          member: createReference(practitioner),
        },
      ],
    } as CareTeam)
  );
}
